import axiosBase from '@/services/axios_base'
import type {
  ORingDesignAssistantCalculateHousingRequest,
  ORingExpertModeCalculateResultRequest,
  ORingExpertModeCheckFreeToleranceRequest,
  ORingExpertModeCheckHousingRequest,
  ORingExpertModeCreateHousingRequest,
  ORingExpertModeGetTolerancesRequest,
  ORingAssistantCalculateHousingRequest,
  ORingAssistantCalculateOptimalORingRequest,
  ORingAssistantCheckHousingRequest,
  ORingAssistantGetDefaultToleranceClassesRequest,
  ORingMaterialAssistantCheckPressureAndGapRequest,
  ORingMaterialAssistantCheckTemperatureRequest,
  ORingMaterialAssistantGetMaterialSearchRequest,
  ORingMaterialAssistantGetMetricRangesRequest,
  ORingMaterialAssistantLoadFluidsRequest,
  ORingMaterialAssistantSearchRequest,
  ORingConfiguratorCheckRingRequest,
  ORingConfiguratorDownloadPdfRequest,
  ORingConfiguratorGetConstructionViewDetailsRequest,
  ORingConfiguratorGetDropdownInfoRequest,
  ORingConfiguratorGetProductDetailsRequest,
  ORingConfiguratorSendMailRequest,
  ORingConfiguratorProductSearchAutoCompleteRequest
} from '@/types/oring-api'
import type {
  Housing,
  ORingCalculateResultExpertModeResponse,
  ORingCheckFreeToleranceExpertModeResponse,
  ORingCheckHousingExpertModeResponse,
  ORingCreateHousingExpertModeResponse,
  ORingGetPreloadsExpertModeResponse,
  ORingGetTolerancesExpertModeResponse,
  ORingCalculateHousingAssistantResponse,
  ORingCalculateOptimalORingAssistantResponse,
  ORingCheckHousingAssistantResponse,
  ORingGetDefaultToleranceClassesAssistantResponse,
  ORingGetPreloadsAssistantResponse,
  ORingCheckPressureAndGapMaterialAssistantResponse,
  ORingCheckTemperatureMaterialAssistantResponse,
  ORingGetMaterialSearchMaterialAssistantResponse,
  ORingGetMetricRangesMaterialAssistantResponse,
  ORingLoadFluidsMaterialAssistantResponse,
  ORingSearchMaterialAssistantResponse,
  ORingCheckRingConfiguratorResponse,
  ORingDownloadPdfConfiguratorResponse,
  ORingGetConstructionViewDetailsConfiguratorResponse,
  ORingGetDropdownInfoConfiguratorResponse,
  ORingGetProductDetailsConfiguratorResponse,
  ORingSendMailConfiguratorResponse,
  ORingProductSearchAutoCompleteConfiguratorResponse
} from '@/types/oring-api-responses'

export const useOringCalculatorService = () => {
  const baseUrl = useRuntimeConfig().public.BASIC_API_URL

  // Create axios instance with special headers (handled by axios_base)
  const oringAxios = axiosBase.create({
    baseURL: baseUrl + '/ORing/1.0.0'
  })

  return {
    // DesignAssistantController
    calculateHousingDesignAssistant: async (
      body: ORingDesignAssistantCalculateHousingRequest
    ): Promise<Housing> => {
      try {
        const res = await oringAxios.post('/DesignAssistant/CalculateHousing', body)
        return res.data
      } catch {
        return {} as Housing
      }
    },

    // ExpertModeController
    calculateResultExpertMode: async (
      body: ORingExpertModeCalculateResultRequest
    ): Promise<ORingCalculateResultExpertModeResponse> => {
      try {
        const res = await oringAxios.post('/ExpertMode/CalculateResult', body)
        return res.data
      } catch {
        return {} as ORingCalculateResultExpertModeResponse
      }
    },
    checkFreeToleranceExpertMode: async (
      body: ORingExpertModeCheckFreeToleranceRequest
    ): Promise<ORingCheckFreeToleranceExpertModeResponse> => {
      try {
        const res = await oringAxios.post('/ExpertMode/CheckFreeTolerance', body)
        return res.data
      } catch {
        return {} as ORingCheckFreeToleranceExpertModeResponse
      }
    },
    checkHousingExpertMode: async (
      body: ORingExpertModeCheckHousingRequest
    ): Promise<ORingCheckHousingExpertModeResponse> => {
      try {
        const res = await oringAxios.post('/ExpertMode/CheckHousing', body)
        return res.data
      } catch {
        return {} as ORingCheckHousingExpertModeResponse
      }
    },
    createHousingExpertMode: async (
      body: ORingExpertModeCreateHousingRequest
    ): Promise<ORingCreateHousingExpertModeResponse> => {
      try {
        const res = await oringAxios.post('/ExpertMode/CreateHousing', body)
        return res.data
      } catch {
        return {} as ORingCreateHousingExpertModeResponse
      }
    },
    getPreloadsExpertMode: async (): Promise<ORingGetPreloadsExpertModeResponse> => {
      try {
        const res = await oringAxios.get('/ExpertMode/GetPreloads')
        return res.data
      } catch {
        return {} as ORingGetPreloadsExpertModeResponse
      }
    },
    getTolerancesExpertMode: async (
      body: ORingExpertModeGetTolerancesRequest
    ): Promise<ORingGetTolerancesExpertModeResponse> => {
      try {
        const res = await oringAxios.post('/ExpertMode/GetTolerances', body)
        return res.data
      } catch {
        return {} as ORingGetTolerancesExpertModeResponse
      }
    },

    // ORingAssistantController
    calculateHousingORingAssistant: async (
      body: ORingAssistantCalculateHousingRequest
    ): Promise<ORingCalculateHousingAssistantResponse> => {
      try {
        const res = await oringAxios.post('/ORingAssistant/CalculateHousing', body)
        return res.data
      } catch {
        return {} as ORingCalculateHousingAssistantResponse
      }
    },
    calculateOptimalORingAssistant: async (
      body: ORingAssistantCalculateOptimalORingRequest
    ): Promise<ORingCalculateOptimalORingAssistantResponse> => {
      try {
        const res = await oringAxios.post('/ORingAssistant/CalculateOptimalORing', body)
        return res.data
      } catch {
        return {} as ORingCalculateOptimalORingAssistantResponse
      }
    },
    checkHousingORingAssistant: async (
      body: ORingAssistantCheckHousingRequest
    ): Promise<ORingCheckHousingAssistantResponse> => {
      try {
        const res = await oringAxios.post('/ORingAssistant/CheckHousing', body)
        return res.data
      } catch {
        return {} as ORingCheckHousingAssistantResponse
      }
    },
    getDefaultToleranceClassesORingAssistant: async (
      body: ORingAssistantGetDefaultToleranceClassesRequest
    ): Promise<ORingGetDefaultToleranceClassesAssistantResponse> => {
      try {
        const res = await oringAxios.post('/ORingAssistant/GetDefaultToleranceClasses', body)
        return res.data
      } catch {
        return {} as ORingGetDefaultToleranceClassesAssistantResponse
      }
    },
    getPreloadsORingAssistant: async (): Promise<ORingGetPreloadsAssistantResponse> => {
      try {
        const res = await oringAxios.get('/ORingAssistant/GetPreloads')
        return res.data
      } catch {
        return {} as ORingGetPreloadsAssistantResponse
      }
    },

    // MaterialAssistantController
    checkPressureAndGapMaterialAssistant: async (
      body: ORingMaterialAssistantCheckPressureAndGapRequest
    ): Promise<ORingCheckPressureAndGapMaterialAssistantResponse> => {
      try {
        const res = await oringAxios.post('/MaterialAssistant/CheckPressureAndGap', body)
        return res.data
      } catch {
        return {} as ORingCheckPressureAndGapMaterialAssistantResponse
      }
    },
    checkTemperatureMaterialAssistant: async (
      body: ORingMaterialAssistantCheckTemperatureRequest
    ): Promise<ORingCheckTemperatureMaterialAssistantResponse> => {
      try {
        const res = await oringAxios.post('/MaterialAssistant/CheckTemperature', body)
        return res.data
      } catch {
        return {} as ORingCheckTemperatureMaterialAssistantResponse
      }
    },
    getMaterialSearchMaterialAssistant: async (
      body: ORingMaterialAssistantGetMaterialSearchRequest
    ): Promise<ORingGetMaterialSearchMaterialAssistantResponse> => {
      try {
        const res = await oringAxios.post('/MaterialAssistant/GetMaterialSearch', body)
        return res.data
      } catch {
        return {} as ORingGetMaterialSearchMaterialAssistantResponse
      }
    },
    getMetricRangesMaterialAssistant: async (
      body: ORingMaterialAssistantGetMetricRangesRequest
    ): Promise<ORingGetMetricRangesMaterialAssistantResponse> => {
      try {
        const res = await oringAxios.post('/MaterialAssistant/GetMetricRanges', body)
        return res.data
      } catch {
        return {} as ORingGetMetricRangesMaterialAssistantResponse
      }
    },
    loadFluidsMaterialAssistant: async (
      body: ORingMaterialAssistantLoadFluidsRequest
    ): Promise<ORingLoadFluidsMaterialAssistantResponse> => {
      try {
        const res = await oringAxios.post('/MaterialAssistant/LoadFluids', body)
        return res.data
      } catch {
        return {} as ORingLoadFluidsMaterialAssistantResponse
      }
    },
    searchMaterialAssistant: async (
      body: ORingMaterialAssistantSearchRequest
    ): Promise<ORingSearchMaterialAssistantResponse> => {
      try {
        const res = await oringAxios.post('/MaterialAssistant/Search', body)
        return res.data
      } catch {
        return {} as ORingSearchMaterialAssistantResponse
      }
    },

    // ORingConfiguratorController
    checkRingORingConfigurator: async (
      body: ORingConfiguratorCheckRingRequest
    ): Promise<ORingCheckRingConfiguratorResponse> => {
      try {
        const res = await oringAxios.post('/ORingConfigurator/CheckRing', body)
        return res.data
      } catch {
        return {} as ORingCheckRingConfiguratorResponse
      }
    },    downloadPdfORingConfigurator: async (
      body: ORingConfiguratorDownloadPdfRequest
    ): Promise<ORingDownloadPdfConfiguratorResponse> => {
      const res = await oringAxios.post('/ORingConfigurator/DownloadPdf', body, {
        responseType: 'blob'
      })
      return res.data
    },
    getConstructionViewDetailsORingConfigurator: async (
      body: ORingConfiguratorGetConstructionViewDetailsRequest
    ): Promise<ORingGetConstructionViewDetailsConfiguratorResponse> => {
      try {
        const res = await oringAxios.post('/ORingConfigurator/GetConstructionViewDetails', body)
        return res.data
      } catch {
        return {} as ORingGetConstructionViewDetailsConfiguratorResponse
      }
    },
    getDropdownInfoORingConfigurator: async (
      body: ORingConfiguratorGetDropdownInfoRequest
    ): Promise<ORingGetDropdownInfoConfiguratorResponse> => {
      try {
        const res = await oringAxios.post('/ORingConfigurator/GetDropdownInfo', body)
        return res.data
      } catch {
        return {} as ORingGetDropdownInfoConfiguratorResponse
      }
    },
    getProductDetailsORingConfigurator: async (
      body: ORingConfiguratorGetProductDetailsRequest
    ): Promise<ORingGetProductDetailsConfiguratorResponse> => {
      try {
        const res = await oringAxios.post('/ORingConfigurator/GetProductDetails', body)
        return res.data
      } catch {
        return {} as ORingGetProductDetailsConfiguratorResponse
      }
    },
    sendMailORingConfigurator: async (
      body: ORingConfiguratorSendMailRequest
    ): Promise<ORingSendMailConfiguratorResponse> => {
      const res = await oringAxios.post('/ORingConfigurator/SendMail', body)
      return res.data
    },
    productSearchAutoCompleteORingConfigurator: async (
      body: ORingConfiguratorProductSearchAutoCompleteRequest
    ): Promise<ORingProductSearchAutoCompleteConfiguratorResponse> => {
      try {
        const res = await oringAxios.post('/ORingConfigurator/ProductSearchAutoComplete', body)
        return res.data
      } catch {
        return {} as ORingProductSearchAutoCompleteConfiguratorResponse
      }
    }
  }
}
